import { format } from 'date-fns';
import { ref, set, get, orderByChild, onValue, equalTo, update, runTransaction, goOffline, goOnline } from 'firebase/database';
import {
  collection,
  getDocsFromCache,
  getDocs,
  setDoc,
  getDoc,
  doc,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import {
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  uploadString,
  listAll,
  uploadBytes,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

import { loadAuthProfile } from './authProfile';
import { recordNewSession } from './sessionHistory';
import { materialsAPI } from '../api/materials-api';
import { firestore, database, MyRef, myStorage, materialCollection } from '../Common/Firebase';
import { throwErrorRollbar } from '../index';
import { setFlagNameAddForm, setFlagNameUpdateForm } from '../redux/slices/flagsSlice';
import {
  setMaterialsState,
  setNotebooksState,
  setStatisticsAllState,
  setStudentsState,
  setTeachersState,
} from '../redux/slices/statistics/additionalStatisticsSlice';
import { setProgressFile, setObjectFiles } from '../redux/slices/uploadFileSlice';
import { setSaveNotebook } from '../redux/slices/userSlice';
import { store } from '../redux/store';

const mergeImages = require('merge-base64');
const PDFJS = require('pdfjs-dist/webpack');

export let subjects = [
  'geography',
  'geometry',
  'historyUkraine',
  'ukrainianLanguage',
  'english',
  'physics',
  'biology',
  'mathematics',
  'nature',
  'algebra',
  // 'infografics',
  'youngSchoolMathematics',
  'allWorldHistory',
  'chemistry',
  'youngSchoolRussianLanguage',
  'ukrainianLiterature',
  'foreignLiterature',
  'youngUkrainianLanguage',
  'german',
  'kvol',
];

export let subjectsUA = [
  'Географія',
  'Геометрія',
  'Історія України',
  'Українська мова',
  'Англiйська мова',
  'Фізика',
  'Біологія',
  'Математика',
  'Природа',
  'Алгебра',
  'Молодша школа - Математика',
  'Всесвітня історія',
  'Хімія',
  'Молодша школа - Російська мова',
  'Українська література',
  'Зарубіжна література',
  'Молодша школа - Українська мова',
  'Німецька мова',
  'КВОЛ',
];

export const getPastTime = monthsToSubtract => {
  const now = new Date();
  now.setMonth(now.getMonth() - monthsToSubtract); // Отнимаем необходимое количество месяцев

  const seconds = Math.floor(now.getTime() / 1000);
  const milliseconds = now.getTime() % 1000;
  const nanoseconds = milliseconds * 1000000;

  const timestamp = {
    seconds: seconds,
    nanoseconds: nanoseconds,
  };

  return timestamp;
};

export const getNowTime = () => {
  const now = new Date();
  const seconds = Math.floor(now.getTime() / 1000);
  const milliseconds = now.getTime() % 1000;
  const nanoseconds = milliseconds * 1000000;
  const timestamp = {
    seconds: seconds,
    nanoseconds: nanoseconds,
  };
  return timestamp;
};

export const getSubjectsStudentSelector = () => {
  const state = store.getState();
  const selectorState = state.studentNames.subjectsStudentObj;
  return selectorState;
};

export const setSaveNotebookFunc = () => {
  const state = store.getState();
  const selectorState = state.userTypeName.userSaveNotebook;
  store.dispatch(setSaveNotebook(!selectorState));
};

export const getSubjectName = async () => {
  const q = query(collection(firestore, 'Materials'));
  const querySnapshot = await getDocs(q);
  const arrNames = [];
  querySnapshot.forEach(doc => {
    arrNames.push(doc.id);
  });
  return arrNames;
};

export async function getMaterials() {
  let materialDocs = await getDocsFromCache(materialCollection);

  if (materialDocs.empty) {
    materialDocs = await getDocs(materialCollection);
  }

  let result = [];
  materialDocs.forEach(doc => result.push({ ...doc.data(), id: doc.id }));

  return result.sort((a, b) => {
    try {
      if (a.subject.localeCompare(b.subject) != 0) return a.subject.localeCompare(b.subject);

      if (a.grade.localeCompare(b.grade) != 0) return a.grade.localeCompare(b.grade);

      if (a.type.localeCompare(b.type) != 0) return a.type.localeCompare(b.type);

      return a.theme.localeCompare(b.theme);
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  });
}

export function subscribeToMaterialsChanges(callback) {
  return onSnapshot(materialCollection, snapshot => {
    let result = [];
    snapshot.forEach(doc => result.push({ ...doc.data(), id: doc.id }));

    result.sort((a, b) => {
      try {
        if (a.subject.localeCompare(b.subject) !== 0) return a.subject.localeCompare(b.subject);
        if (a.grade.localeCompare(b.grade) !== 0) return a.grade.localeCompare(b.grade);
        return a.theme.localeCompare(b.theme);
      } catch (error) {
        console.log(error);
        throwErrorRollbar(error);
      }
    });

    // Вызов callback с обновленными данными
    callback(result);
  });
}

export const createTemporaryLink = (id, url, pageNum, type) => {
  const now = new Date();
  now.setHours(now.getHours() + 3);
  const expireAt = now;
  set(ref(database, 'Links/' + id), {
    url,
    pageNum,
    type,
    expireAt: expireAt.toISOString(),
  });
};

export const createPermanentLink = (id, url, pageNum, type) => {
  return update(ref(database, 'Links/' + id), {
    url,
    pageNum,
    type,
    permanent: true,
  });
};

export function getUserFromDb(user) {
  return loadAuthProfile(user, path => get(ref(database, path)));
}

export function getLink(id) {
  return get(ref(database, 'Links/' + id));
}

const getFirstPageAsImage = async url => {
  const response = await fetch(url);
  const pdfData = await response.arrayBuffer();
  const pdf = await PDFJS.getDocument({ data: pdfData }).promise;

  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1 });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  const renderContext = {
    canvasContext: ctx,
    viewport: viewport,
  };

  await page.render(renderContext).promise;
  const dataURL = canvas.toDataURL('image/webp', 1);

  const byteString = atob(dataURL.split(',')[1]);

  const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([ab], { type: mimeString });

  return blob;
};

export const addMaterialItem = async (selectedName, grade, lang, name, theme, type, file, mail) => {
  //const selectedNameCheck = selectedName === 'Англiйська мова' ? 'Англiйська мова Два' : selectedName;
  const objNames = {
    grade: grade ? grade + '/' : '',
    lang: lang ? lang + '/' : '',
    name: name ? name + '/' : '',
    theme: theme ? theme + '/' : '',
    subject: selectedName ? selectedName + '/' : '',
    type: type ? type + '/' : '',
    mail: mail ? mail + '/' : '',
  };
  const metadata = {
    md5Hash: 'YES',
    cacheControl: 'YES',
    contentDisposition: 'YES',
    updated: 'YES',
  };

  let typeDirFile = file && file.name.split('.')[file.name.split('.').length - 1] === 'pdf' ? '' : 'doc/';

  const storageRef = MyRef(
    myStorage,
    'Материали Логос/' + objNames.subject + objNames.grade + objNames.theme + typeDirFile + file.name + objNames.mail,
  );
  const uploadTask = uploadBytesResumable(storageRef, file, metadata);

  uploadTask.on(
    'state_changed',
    snapshot => {
      switch (snapshot.state) {
        case 'paused':
          console.log('Upload is paused');
          break;
        case 'running':
          console.log('Upload is running');
          break;
      }
    },
    error => {
      switch (error.code) {
        case 'storage/unauthorized':
          break;
        case 'storage/canceled':
          break;
        case 'storage/unknown':
          break;
      }
    },
    () => {
      // Upload completed successfully, now we can get the download URL
      getDownloadURL(uploadTask.snapshot.ref).then(async downloadURL => {
        let downloadURLfile = downloadURL;

        const imgBlob = await getFirstPageAsImage(downloadURL);

        const storageImgRef = MyRef(
          myStorage,
          'Материали Логос/' +
            objNames.subject +
            objNames.grade +
            objNames.theme +
            'thum/' +
            typeDirFile +
            file.name +
            objNames.mail,
        );

        uploadBytes(storageImgRef, imgBlob).then(snapshot => {
          getDownloadURL(storageImgRef).then(async url => {
            const newMaterial = {
              grade,
              lang,
              name,
              subject: selectedName,
              theme,
              thum: url,
              type: type,
              url: file ? downloadURLfile : null,
              mail,
              createTime: getNowTime(),
            };

            try {
              await materialsAPI.addMaterial(newMaterial);

              store.dispatch(setFlagNameAddForm(true));

              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const months = [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
              ];
              const currentMonth = months[currentDate.getMonth()];

              const res = await get(ref(database, `Statistics/AllMaterials/${currentYear}/${currentMonth}`));
              const currentValue = res.val();
              const updatedValue = currentValue + 1;
              const realtimeDoc = `Statistics/AllMaterials/${currentYear}`;

              await update(ref(database, realtimeDoc), {
                [currentMonth]: updatedValue,
              });
            } catch (error) {
              throwErrorRollbar(error);
            }
          });
        });
      });
    },
  );
};

export const updateMaterialItem = async (
  selectedName,
  id,
  grade,
  lang,
  name,
  theme,
  fileImg,
  type,
  file,
  typeAction,
  oldPathFile,
  mail,
) => {
  const objNames = {
    grade: grade ? grade + '/' : '',
    lang: lang ? lang + '/' : '',
    name: name ? name + '/' : '',
    theme: theme ? theme + '/' : '',
    subject: selectedName ? selectedName + '/' : '',
    type: type ? type + '/' : '',
    mail: mail ? mail + '/' : '',
  };

  const metadata = {
    md5Hash: 'YES',
    cacheControl: 'YES',
    contentDisposition: 'YES',
    updated: 'YES',
  };

  if (file) {
    const storageRef = MyRef(myStorage, oldPathFile.url);
    const stringDelete = storageRef._location.path_;

    const desertRef = MyRef(myStorage, stringDelete);

    await deleteObject(desertRef)
      .then(() => {
        console.log('deleted');
      })
      .catch(error => {
        console.log(error);
        throwErrorRollbar(error);
      });

    const storageRefImg = MyRef(myStorage, oldPathFile.thum);
    const stringDeleteImg = storageRefImg._location.path_;

    const desertRefImg = MyRef(myStorage, stringDeleteImg);

    if (!stringDeleteImg.includes('data:image/webp;base64')) {
      await deleteObject(desertRefImg)
        .then(() => {
          console.log('deleted');
        })
        .catch(error => {
          console.log(error);
          throwErrorRollbar(error);
        });
    }

    let typeDirFile = file && file.name.split('.')[file.name.split('.').length - 1] === 'pdf' ? '' : 'doc/';

    const storageRefNew = MyRef(
      myStorage,
      'Материали Логос/' + objNames.subject + objNames.grade + objNames.theme + typeDirFile + file.name,
      objNames.mail,
    );

    const uploadTask = uploadBytesResumable(storageRefNew, file, metadata);
    uploadTask.on(
      'state_changed',
      snapshot => {
        switch (snapshot.state) {
          case 'paused':
            console.log('Upload is paused');
            break;
          case 'running':
            console.log('Upload is running');
            break;
        }
      },
      error => {
        switch (error.code) {
          case 'storage/unauthorized':
            break;
          case 'storage/canceled':
            break;
          case 'storage/unknown':
            break;
        }
      },
      () => {
        // Upload completed successfully, now we can get the download URL
        getDownloadURL(uploadTask.snapshot.ref).then(async downloadURL => {
          let downloadURLfile = downloadURL;

          const imgBlob = await getFirstPageAsImage(downloadURLfile);

          const storageRefNewThum = MyRef(
            myStorage,
            'Материали Логос/' + objNames.subject + objNames.grade + objNames.theme + 'thum/' + typeDirFile + file.name,
            objNames.mail,
          );

          uploadBytes(storageRefNewThum, imgBlob).then(snapshot => {
            console.log('Thumb updated!');
            getDownloadURL(storageRefNewThum).then(async url => {
              const newData = {
                grade,
                lang,
                name,
                subject: selectedName,
                theme,
                thum: url,
                type,
                url: downloadURLfile,
                mail,
                createTime: getNowTime(),
              };

              try {
                await materialsAPI.updateMaterial(id, newData);

                store.dispatch(setFlagNameUpdateForm(true));
              } catch (error) {
                console.log(error);
                throwErrorRollbar(error);
              }

              const refDb = ref(database, 'Students');
              console.log('update!');

              get(refDb).then(snapshot => {
                console.log('update!');
                const students = Object.entries(snapshot.val());
                students.forEach(student => {
                  if (student[1].materials && student[1].materials[id]) {
                    const refStudentMat = ref(database, `Students/${student[0]}/materials/${id}`);

                    update(refStudentMat, {
                      grade: grade,
                      lang: lang,
                      name: name,
                      theme: theme,
                      url: downloadURLfile,
                      thum: url,
                    });
                  }
                });
              });
            });
          });
        });
      },
    );
  } else {
    const refDb = ref(database, 'Students');

    get(refDb).then(snapshot => {
      const students = Object.entries(snapshot.val());
      students.forEach(student => {
        if (student[1].materials && student[1].materials[id]) {
          const refStudentMat = ref(database, `Students/${student[0]}/materials/${id}`);
          update(refStudentMat, {
            grade: grade,
            lang: lang,
            name: name,
            theme: theme,
          });
        }
      });
    });

    const newData = {
      grade,
      lang,
      name,
      subject: selectedName,
      theme,
      thum: oldPathFile.thum,
      type,
      url: !file ? oldPathFile.url : '',
      mail,
      createTime: getNowTime(),
    };

    try {
      await materialsAPI.updateMaterial(id, newData);

      store.dispatch(setFlagNameUpdateForm(true));
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  }
};

export const removeMaterialItem = async (id, fileImg, file) => {
  if (file) {
    const storageRef = MyRef(myStorage, file);
    const stringDelete = storageRef._location.path_;

    const desertRef = MyRef(myStorage, stringDelete);

    try {
      await deleteObject(desertRef);

      console.log('deleted');

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const currentMonth = months[currentDate.getMonth()];

      const res = await get(ref(database, `Statistics/AllMaterials/${currentYear}/${currentMonth}`));

      const currentValue = res.val() || 0;

      const updatedValue = currentValue - 1 > 0 ? currentValue - 1 : 0;

      await update(ref(database, `Statistics/AllMaterials/${currentYear}`), {
        [currentMonth]: updatedValue,
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }

    const storageRefImg = MyRef(myStorage, fileImg);
    const stringDeleteImg = storageRefImg._location.path_;
    const desertRefImg = MyRef(myStorage, stringDeleteImg);

    if (!stringDeleteImg.includes('data:image/webp;base64')) {
      try {
        await deleteObject(desertRefImg)
          .then(() => {
            console.log('deleted');
          })
          .catch(error => {
            console.log(error);
            throwErrorRollbar(error);
          });
      } catch (error) {
        console.log(error);
        throwErrorRollbar(error);
      }
    }
  }

  try {
    await materialsAPI.removeMaterial(id);

    store.dispatch(setFlagNameUpdateForm(true));
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getHomework = selectItem => {
  return new Promise((resolve, reject) => {
    if (selectItem) {
      const mostViewedPosts = query(ref(database, 'Homeworks'), orderByChild('subject'), equalTo(selectItem));
      onValue(mostViewedPosts, snapshot => {
        const data = snapshot.val();
        return resolve(data);
      });
    } else {
      const mostViewedPosts = query(ref(database, 'Homeworks'), orderByChild('subject'));
      onValue(mostViewedPosts, snapshot => {
        const data = snapshot.val();
        return resolve(data);
      });
    }
  });
};

export const getStudents = (selectItem, ageFilter, cityFilter, classFilter, subjectFilter) => {
  return new Promise((resolve, reject) => {
    let studentsRef = ref(database, 'Students');
    let queryRef;
    if (selectItem) {
      queryRef = query(studentsRef, orderByChild('branch'), equalTo(selectItem));
    } else {
      queryRef = query(studentsRef, orderByChild('FIO'));
    }
    onValue(queryRef, snapshot => {
      const data = snapshot.val();
      let filteredDataOld = Object.values(data).filter(item => !item.FIO);
      // need to check this variable in future it returns array with session time and some ids
      let filteredData = Object.values(data || {}).filter(
        student => student?.type === 'Student' && typeof student?.FIO === 'string',
      );
      if (ageFilter) {
        filteredData = filteredData.filter(student => student.age === ageFilter);
      }
      if (cityFilter) {
        filteredData = filteredData.filter(student => student.city === cityFilter);
      }
      if (subjectFilter) {
        filteredData = filteredData.filter(student => student.subjects[subjectFilter].activeSub === true);
      }
      if (classFilter) {
        filteredData = filteredData.filter(student => student.classStudent === classFilter);
      }
      return resolve(filteredData);
    });
  });
};

export const getStudentsGroup = subjectFilter => {
  return new Promise((resolve, reject) => {
    const studentsRef = ref(database, 'StudentsGroup');
    let queryRef = query(studentsRef, orderByChild('groupName'));

    onValue(
      queryRef,
      snapshot => {
        const data = snapshot.val();

        if (!data) return resolve([]);
        let filteredData = Object.values(data);

        if (subjectFilter) {
          filteredData = filteredData.filter(
            student =>
              student.subjects && student.subjects[subjectFilter] && student.subjects[subjectFilter].activeSub === true,
          );
        }

        resolve(filteredData);
      },
      error => {
        console.error('Error fetching students group:', error);
        reject(error);
      },
    );
  });
};

export const getStudentView = async id => {
  try {
    const res = await get(ref(database, `Students/${id}`));
    return res;
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getStudentsGroupView = async id => {
  try {
    const res = await get(ref(database, `StudentsGroup/${id}`));
    return res;
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getScheduleTeacher = async (id, name) => {
  try {
    const res = await get(ref(database, `Students/${id}/schedule/${name}`));
    return res;
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getTeachers = (selectItem, cityFilter) => {
  return new Promise((resolve, reject) => {
    let teachersRef = ref(database, 'Teachers');
    let queryRef;
    if (selectItem) {
      queryRef = query(teachersRef, orderByChild('branch'), equalTo(selectItem));
    } else {
      queryRef = query(teachersRef, orderByChild('FIO'));
    }
    onValue(queryRef, snapshot => {
      const data = snapshot.val();
      let filteredData = Object.values(data);
      if (cityFilter) {
        filteredData = filteredData.filter(teacher => teacher.city === cityFilter);
      }
      return resolve(filteredData);
    });
  });
};

export const getStatistics = () => {
  return new Promise((resolve, reject) => {
    let statisticsRef = ref(database, 'Statistics');
    let queryRef = query(statisticsRef);

    onValue(queryRef, snapshot => {
      const data = snapshot.val();
      return resolve(data);
    });
  });
};

// TODO: mechanism for transferring Sups to Teachers
// export const getSups = () => {
//   return new Promise((resolve, reject) => {
//     let supsRef = ref(database, 'Sups');
//     let queryRef = query(supsRef, orderByChild('FIO'));
//     onValue(queryRef, (snapshot) => {
//       const data = snapshot.val();
//       const keysAndValues = [];

//       snapshot.forEach((childSnapshot) => {
//         const key = childSnapshot.key;
//         const value = childSnapshot.val();
//         keysAndValues.push({ key, value });
//       });

//       return resolve(keysAndValues);
//     }, (error) => {
//       console.error('Error fetching Sups: ', error);
//       return reject(error);
//     });
//   });
// };

export const getTeacherView = async id => {
  try {
    const res = await get(ref(database, `Teachers/${id}`));
    return res;
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getScheduleStudents = async id => {
  try {
    const res = await get(ref(database, `Students/${id}/schedule`));
    return res;
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getTeacherScheduleBoard = async id => {
  try {
    const res = await get(ref(database, `Teachers/${id}/schedule`));
    return res;
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getTeacher = async id => {
  try {
    const res = await get(ref(database, `Teachers/${id}`));
    return res;
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const changeStudentActive = async id => {
  try {
    const res = await set(ref(database, `Students/${id}`));
    return res;
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const setStudent = (currentHomework, student) => {
  const id = student.email.replace(/\./g, '');
  return update(ref(database, `Students/${id}/homeworks`), {
    [currentHomework.name + id]: {
      content: currentHomework.content + id,
      id: currentHomework.id + id,
      name: currentHomework.name,
      notebook: currentHomework.notebook,
      subject: subjectsObj[currentHomework.subject],
      completed: currentHomework.completed,
      deadline: currentHomework.deadline,
    },
  });
};

export const setStudentHomework = (currentHomework, student, numbHomework, deadline, sendersEmail) => {
  const mailStudent = student.email.replace(/\./g, '');
  const idMail = currentHomework.id + mailStudent;
  (async () => {
    const docRef = doc(firestore, 'homeworks-content', currentHomework.id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      data.id = idMail;
      setDoc(doc(firestore, 'homeworks-content-student/', idMail), data);
    } else {
      console.log('No such document!');
    }
  })();

  try {
    const id = student.email.replace(/\./g, '');
    update(ref(database, `Students/${id}/homeworks`), {
      [currentHomework.id + id]: {
        content: `homeworks-content-student/${currentHomework.id + id}`,
        id: currentHomework.id + id,
        name: currentHomework.name,
        mail: sendersEmail,
        titles: currentHomework.titles,
        notebook: currentHomework.notebook,
        subject: subjectsObj[currentHomework.subject],
        completed: currentHomework.completed,
        deadline: deadline,
        subItems: currentHomework.subItems,
        subArrayCheckeds: currentHomework.subArrayCheckeds,
        studentPhotos: false,
        countNumbHomework: numbHomework,
        teacherCheckeds: currentHomework.teacherCheckeds,
      },
    });
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }

  try {
    const id = student.email.replace(/\./g, '');
    const now = new Date();
    const formattedTime = format(now, 'dd MM yyyy HH:mm');

    update(ref(database, `Students/${id}/notification`), {
      [currentHomework.id + id]: {
        id: currentHomework.id + id,
        idItem: currentHomework.id + id,
        name: currentHomework.name,
        subject: subjectsObj[currentHomework.subject],
        time: formattedTime,
        type: 'homeworks',
        viewed: true,
      },
    });
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const setStudentHomeworkTeacherChacked = (mail, subject, id, teacherCheckeds) => {
  try {
    let teacherCheckedsJSON;

    try {
      JSON.parse(teacherCheckeds); // Пробуем распарсить
      teacherCheckedsJSON = teacherCheckeds; // Если успешно, то это уже JSON
    } catch (error) {
      teacherCheckedsJSON = JSON.stringify(teacherCheckeds); // Если ошибка, то конвертируем в JSON
    }

    return update(ref(database, `Students/${mail}/homeworks/${id}`), {
      teacherCheckeds: teacherCheckedsJSON,
    });
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const setStudentMaterial = (currentMaterial, student, deadline, mail) => {
  if ('chapter' in currentMaterial) {
    try {
      const id = student.email.replace(/\./g, '');
      const now = new Date();
      const formattedTime = format(now, 'dd MM yyyy HH:mm');
      const startDateExam = format(now, 'dd.MM.yyyy');
      const deadlineTime = format(deadline, 'dd.MM.yyyy');

      update(ref(database, `Students/${id}/materials`), {
        [currentMaterial.id]: {
          chapter: Object.values(currentMaterial.chapter),
          conditions: currentMaterial.conditions,
          courseCompletion: currentMaterial.courseCompletion,
          expirationData: currentMaterial.expirationData,
          grade: currentMaterial.grade,
          id: currentMaterial.id,
          lang: currentMaterial.lang,
          mail: currentMaterial.mail === mail ? currentMaterial.mail : mail,
          name: currentMaterial.name,
          purpose: currentMaterial.purpose,
          startDate: currentMaterial.startDate,
          subject: currentMaterial.subject,
          theme: currentMaterial.theme,
          thum: currentMaterial.thum ? currentMaterial.thum : false,
          type: currentMaterial.type,
          url: currentMaterial.url,
          examNumber: currentMaterial.examNumber,
          deadline: deadlineTime,
          startDateExam: startDateExam,
        },
      });

      update(ref(database, `Students/${id}/notification`), {
        [currentMaterial.id]: {
          id: currentMaterial.id,
          idItem: currentMaterial.id,
          name: currentMaterial.name,
          subject: subjectsObj[currentMaterial.subject],
          time: formattedTime,
          type: 'materials interative-exum',
          subType: currentMaterial.type,
          viewed: true,
        },
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    try {
      const id = student.email.replace(/\./g, '');
      const now = new Date();
      const formattedTime = format(now, 'dd MM yyyy HH:mm');

      update(ref(database, `Students/${id}/materials`), {
        [currentMaterial.id]: {
          grade: currentMaterial.grade,
          id: currentMaterial.id,
          name: currentMaterial.name,
          mail: currentMaterial.mail === mail ? currentMaterial.mail : mail,
          lang: currentMaterial.lang,
          subject: currentMaterial.subject,
          theme: currentMaterial.theme,
          thum: currentMaterial.thum ? currentMaterial.thum : false,
          type: currentMaterial.type,
          url: currentMaterial.url,
        },
      });

      update(ref(database, `Students/${id}/notification`), {
        [currentMaterial.id]: {
          id: currentMaterial.id,
          idItem: currentMaterial.id,
          name: currentMaterial.name,
          subject: subjectsObj[currentMaterial.subject],
          time: formattedTime,
          type: 'materials',
          subType: currentMaterial.type,
          viewed: true,
        },
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  }
};

const getHomeworkQuery = (student, subjectItem) => {
  const homeworksRef = ref(database, `Students/${student}/homeworks`);
  return subjectItem
    ? query(homeworksRef, orderByChild('subject'), equalTo(subjectItem))
    : query(homeworksRef, orderByChild('subject'));
};

export const setMaterialToGroup = (currentMaterial, groupOfStudents, deadline, selectedStudentsGroup) => {
  if ('chapter' in currentMaterial) {
    try {
      const now = new Date();
      const startDateExam = format(now, 'dd.MM.yyyy');
      const deadlineTime = format(deadline, 'dd.MM.yyyy');
      const groupId = selectedStudentsGroup.id;

      update(ref(database, `StudentsGroup/${groupId}/materials`), {
        [currentMaterial.id]: {
          chapter: Object.values(currentMaterial.chapter),
          conditions: currentMaterial.conditions,
          courseCompletion: currentMaterial.courseCompletion,
          expirationData: currentMaterial.expirationData,
          grade: currentMaterial.grade,
          id: currentMaterial.id,
          lang: currentMaterial.lang,
          mail: currentMaterial.mail,
          name: currentMaterial.name,
          purpose: currentMaterial.purpose,
          startDate: currentMaterial.startDate,
          subject: currentMaterial.subject,
          theme: currentMaterial.theme,
          thum: currentMaterial.thum ? currentMaterial.thum : false,
          type: currentMaterial.type,
          url: currentMaterial.url,
          examNumber: currentMaterial.examNumber,
          deadline: deadlineTime,
          startDateExam: startDateExam,
        },
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    try {
      const groupId = selectedStudentsGroup.id;

      update(ref(database, `StudentsGroup/${groupId}/materials`), {
        [currentMaterial.id]: {
          grade: currentMaterial.grade,
          id: currentMaterial.id,
          name: currentMaterial.name,
          lang: currentMaterial.lang,
          subject: currentMaterial.subject,
          theme: currentMaterial.theme,
          thum: currentMaterial.thum ? currentMaterial.thum : false,
          type: currentMaterial.type,
          url: currentMaterial.url,
        },
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  }

  groupOfStudents.forEach(async student => {
    if ('chapter' in currentMaterial) {
      try {
        const id = student;
        const now = new Date();
        const formattedTime = format(now, 'dd MM yyyy HH:mm');
        const startDateExam = format(now, 'dd.MM.yyyy');
        const deadlineTime = format(deadline, 'dd.MM.yyyy');

        update(ref(database, `Students/${id}/materials`), {
          [currentMaterial.id]: {
            chapter: Object.values(currentMaterial.chapter),
            conditions: currentMaterial.conditions,
            courseCompletion: currentMaterial.courseCompletion,
            expirationData: currentMaterial.expirationData,
            grade: currentMaterial.grade,
            id: currentMaterial.id,
            lang: currentMaterial.lang,
            mail: currentMaterial.mail,
            name: currentMaterial.name,
            purpose: currentMaterial.purpose,
            startDate: currentMaterial.startDate,
            subject: currentMaterial.subject,
            theme: currentMaterial.theme,
            thum: currentMaterial.thum ? currentMaterial.thum : false,
            type: currentMaterial.type,
            url: currentMaterial.url,
            examNumber: currentMaterial.examNumber,
            deadline: deadlineTime,
            startDateExam: startDateExam,
          },
        });

        update(ref(database, `Students/${id}/notification`), {
          [currentMaterial.id]: {
            id: currentMaterial.id,
            idItem: currentMaterial.id,
            name: currentMaterial.name,
            subject: subjectsObj[currentMaterial.subject],
            time: formattedTime,
            type: 'materials interative-exum',
            subType: currentMaterial.type,
            viewed: true,
          },
        });
      } catch (error) {
        console.log(error);
        throwErrorRollbar(error);
      }
    } else {
      try {
        const id = student;
        console.log('id: ', id);
        const now = new Date();
        const formattedTime = format(now, 'dd MM yyyy HH:mm');

        update(ref(database, `Students/${id}/materials`), {
          [currentMaterial.id]: {
            grade: currentMaterial.grade,
            id: currentMaterial.id,
            name: currentMaterial.name,
            lang: currentMaterial.lang,
            subject: currentMaterial.subject,
            theme: currentMaterial.theme,
            thum: currentMaterial.thum ? currentMaterial.thum : false,
            type: currentMaterial.type,
            url: currentMaterial.url,
          },
        });

        update(ref(database, `Students/${id}/notification`), {
          [currentMaterial.id]: {
            id: currentMaterial.id,
            idItem: currentMaterial.id,
            name: currentMaterial.name,
            subject: subjectsObj[currentMaterial.subject],
            time: formattedTime,
            type: 'materials',
            subType: currentMaterial.type,
            viewed: true,
          },
        });
      } catch (error) {
        console.log(error);
        throwErrorRollbar(error);
      }
    }
  });
};

export const getStudentHomework = (student, subjectItem, callback) => {
  let materialsData = null;
  let homeworkData = null;

  // Запрос к materials
  const materialsRef = ref(database, `Students/${student}/materials`);
  onValue(
    materialsRef,
    snapshot => {
      materialsData = snapshot.val() || {};

      // Если данные по домашке уже пришли, то вызываем callback
      if (homeworkData !== null) {
        callback({ data: homeworkData, exumsContent: materialsData });
      }
    },
    error => {
      console.error('Error fetching student materials:', error);
    },
  );

  // Запрос к homework
  const homeworkQuery = getHomeworkQuery(student, subjectItem);
  onValue(
    homeworkQuery,
    snapshot => {
      homeworkData = snapshot.val() || {};

      // Если данные по материалам уже пришли, то вызываем callback
      if (materialsData !== null) {
        callback({ data: homeworkData, exumsContent: materialsData });
      }
    },
    error => {
      console.error('Error fetching student homeworks:', error);
    },
  );
};

export const getStudentHomeworkHM = (student, subjectItem) => {
  return new Promise((resolve, reject) => {
    if (subjectItem) {
      const mostViewedPosts = query(
        ref(database, `Students/${student}/homeworks`),
        orderByChild('subject'),
        equalTo(subjectItem),
      );
      onValue(mostViewedPosts, snapshot => {
        const data = snapshot.val();
        return resolve(data);
      });
    } else {
      const mostViewedPosts = query(ref(database, `Students/${student}/homeworks`), orderByChild('subject'));
      onValue(mostViewedPosts, snapshot => {
        const data = snapshot.val();
        return resolve(data);
      });
    }
  });
};

export const setCheckedHomework = (mail, elementHomework, checkArray, isChecked, subItems, id, subIndex) => {
  try {
    const now = new Date();
    const formattedTime = format(now, 'dd MM yyyy HH:mm');
    if (subItems) {
      update(ref(database, `Students/${mail}/homeworks/${id}`), {
        subArrayCheckeds: checkArray,
      });

      if (isChecked) {
        update(ref(database, `Teachers/${elementHomework[subIndex].mail}/notification`), {
          [id + subIndex]: {
            id: id + subIndex,
            idItem: id,
            mail: mail,
            name: elementHomework[subIndex].name,
            titles: elementHomework[subIndex].titles,
            time: formattedTime,
            point: subIndex + 1,
            subject: elementHomework[subIndex].subject,
            type: 'homeworks-student homeworks-points setItemPointHomeworkChecked',
            viewed: true,
          },
        });
      }
    } else {
      update(ref(database, `Students/${mail}/homeworks/${id}`), {
        completed: isChecked,
      });

      if (isChecked) {
        update(ref(database, `Teachers/${elementHomework.mail}/notification`), {
          [id]: {
            id: id,
            idItem: id,
            mail: mail,
            name: elementHomework.name === undefined ? elementHomework.homeworkName : elementHomework.name,
            time: formattedTime,
            subject: elementHomework.subject,
            type: 'homeworks-student homework setItemPointHomeworkChecked',
            viewed: true,
          },
        });
      }
    }
  } catch (error) {
    console.error('Error updating the database:', error);
  }
};

export const subjectsObj = {
  Географія: 'geography',
  География: 'geography',
  Геометрія: 'geometry',
  Геометрия: 'geometry',
  'Історія України': 'historyUkraine',
  'История Украины': 'historyUkraine',
  'Українська мова': 'ukrainianLanguage',
  'Украинский язык': 'ukrainianLanguage',
  'Англійська мова': 'english',
  'Английский язык': 'english',
  Фізика: 'physics',
  Физика: 'physics',
  Біологія: 'biology',
  Биология: 'biology',
  Математика: 'mathematics',
  Природа: 'nature',
  Алгебра: 'algebra',
  'Молодша школа - Математика': 'youngSchoolMathematics',
  'Младшая школа - Математика': 'youngSchoolMathematics',
  'Всесвітня історія': 'allWorldHistory',
  'Всемирная история': 'allWorldHistory',
  Хімія: 'chemistry',
  Химия: 'chemistry',
  'Молодша школа - Російська мова': 'youngSchoolRussianLanguage',
  'Младшая школа - Русский язык': 'youngSchoolRussianLanguage',
  'Українська література': 'ukrainianLiterature',
  'Украинская литература': 'ukrainianLiterature',
  'Зарубіжна література': 'foreignLiterature',
  'Зарубежная литература': 'foreignLiterature',
  'Молодша школа - Українська мова': 'youngUkrainianLanguage',
  'Младшая школа - Украинский язык': 'youngUkrainianLanguage',
  'Німецька мова': 'german',
  'Немецкий язык': 'german',
  КВОЛ: 'kvol',
};

export const subjectsObjKey = {
  geography: 'Географія',
  geometry: 'Геометрія',
  historyUkraine: 'Історія України',
  ukrainianLanguage: 'Українська мова',
  english: 'Англійська мова',
  physics: 'Фізика',
  biology: 'Біологія',
  mathematics: 'Математика',
  nature: 'Природа',
  algebra: 'Алгебра',
  youngSchoolMathematics: 'Молодша школа - Математика',
  allWorldHistory: 'Всесвітня історія',
  chemistry: 'Хімія',
  youngSchoolRussianLanguage: 'Молодша школа - Російська мова',
  ukrainianLiterature: 'Українська література',
  foreignLiterature: 'Зарубіжна література',
  youngUkrainianLanguage: 'Молодша школа - Українська мова',
  german: 'Німецька мова',
  kvol: 'КВОЛ',
};

const generateParagraphs = num => {
  const paragraphs = Array(num).fill('<p><br></p>');
  return paragraphs.join('');
};

const typeNotebooks = name => {
  switch (name) {
    case 'geography':
      return true;
    case 'geometry':
      return true;
    case 'historyUkraine':
      return false;
    case 'ukrainianLanguage':
      return false;
    case 'english':
      return false;
    case 'physics':
      return true;
    case 'biology':
      return false;
    case 'mathematics':
      return true;
    case 'nature':
      return false;
    case 'algebra':
      return true;
    case 'youngSchoolMathematics':
      return true;
    case 'allWorldHistory':
      return false;
    case 'chemistry':
      return true;
    case 'youngSchoolRussianLanguage':
      return false;
    case 'ukrainianLiterature':
      return false;
    case 'foreignLiterature':
      return false;
    case 'youngUkrainianLanguage':
      return false;
    case 'german':
      return false;
    case 'kvol':
      return false;
  }
};

export const createNotebooksFunc = async (mail, subject) => {
  const id = uuidv4();
  const now = new Date();
  const dateTimeParsing = format(now.setHours(now.getHours()), 'dd.MM.yyyy');
  const userRef = doc(firestore, 'notebook-content/', mail);
  const datta = {
    [id]: {
      firstContent: typeNotebooks(subject) ? generateParagraphs(0) : generateParagraphs(0),
      secondContent: typeNotebooks(subject) ? generateParagraphs(0) : generateParagraphs(0),
    },
  };
  await setDoc(userRef, { [subject]: datta }, { merge: true })
    .then(() => {})
    .catch(error => {
      console.error('Error adding object ttt to document testUser:', error);
      error.name = 'Error adding object ttt to document testUser:';
      throwErrorRollbar(error);
    });

  await set(ref(database, `Notebooks/${mail}/${subject}/${id}`), {
    spreadOrder: 1,
    firstSheet: {
      content: id,
      imageUrl: 'null',
      isReviewed: false,
      lastModifiedDate: dateTimeParsing,
      order: 1,
      widthFirstImg: 100,
    },
    id: id,
    imageUrl: 'null',
    isSquared: typeNotebooks(subject),
    nextId: null,
    prevId: null,
    widthFullImg: 100,
    secondSheet: {
      content: id,
      imageUrl: 'null',
      isReviewed: false,
      lastModifiedDate: dateTimeParsing,
      order: 2,
      widthSecondImg: 100,
    },
  });
};

export const getFuncNotebooks = async mail => {
  try {
    const res = await get(ref(database, `Notebooks/${mail}`));
    return res.val();
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getMaterialsStudent = async mail => {
  try {
    const res = await get(ref(database, `Students/${mail}/materials`));
    return res.val();
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const setExamImage = async (file, mail, subject) => {
  try {
    const id = uuidv4();

    const storageRef = MyRef(myStorage, 'Экзаменационные-работы/' + id + '/' + id + 'image');

    const base64Data = file;
    const metadata = {
      contentType: 'image/png',
    };

    uploadString(storageRef, base64Data, 'data_url', metadata)
      .then(snapshot => {
        console.log('Image uploaded successfully');
        return getDownloadURL(storageRef);
      })
      .then(downloadURL => {
        if (downloadURL.length > 0) {
          update(ref(database, `Students/${mail}/exams`), {
            [id]: {
              id: id,
              url: downloadURL,
              subject: subject.subject,
              theme: subject.theme,
              grade: subject.grade,
              name: subject.name,
              type: subject.type,
            },
          });
        }
      })
      .catch(error => {
        console.error('Error uploading image:', error);
        error.name = 'Error uploading image: ' + error.name;
        throwErrorRollbar(error);
      });
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const setItemPointExumChecked = (e, mail, examItem, purentIndex, pointIndex) => {
  const isChecked = e.target.checked; // это будет либо true, либо false в зависимости от состояния чекбокса
  const currentDate = format(new Date(), 'dd.MM.yyyy');

  const obj = examItem;

  // Проверяем существование необходимых свойств
  if (
    obj.chapter &&
    obj.chapter[purentIndex] &&
    obj.chapter[purentIndex].points &&
    obj.chapter[purentIndex].points[pointIndex]
  ) {
    // Создаем копию объекта
    const updatedObj = { ...obj };
    const targetPoint = updatedObj.chapter[purentIndex].points[pointIndex];

    // Устанавливаем или обновляем свойство itemComplited
    targetPoint.itemComplited = isChecked;

    // Если чекбокс активирован, добавляем текущую дату, иначе устанавливаем значение в null
    targetPoint.dataItemComplited = isChecked ? currentDate : null;

    console.log(updatedObj);

    try {
      const now = new Date();
      const formattedTime = format(now, 'dd MM yyyy HH:mm');
      const chapter = updatedObj.chapter;
      update(ref(database, `Students/${mail}/materials/${obj.id}`), {
        chapter,
      });

      console.log(examItem.mail);

      if (isChecked) {
        update(ref(database, `Teachers/${examItem.mail}/notification`), {
          [examItem.id + 1]: {
            id: examItem.id + 1,
            idItem: examItem.id,
            mail: mail,
            name: examItem.name,
            time: formattedTime,
            point: pointIndex + 1,
            subject: subjectsObj[examItem.subject],
            type: 'materials interative-exum setItemPointExumChecked',
            viewed: true,
          },
        });
      } else {
        update(ref(database, `Teachers/${examItem.mail}/notification`), {
          [examItem.id + 1]: {
            id: examItem.id + 1,
            idItem: examItem.id,
            mail: mail,
            name: examItem.name,
            time: formattedTime,
            point: pointIndex + 1,
            subject: subjectsObj[examItem.subject],
            type: 'materials interative-exum setItemNotPointExumChecked',
            viewed: true,
          },
        });
      }
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    console.error('One of the properties does not exist!');
    const error = new Error('One of the properties does not exist!');
    throwErrorRollbar(error);
  }
};

export const setDateVerificationExem = (e, mail = location.state.studentId, examItem, purentIndex, pointIndex) => {
  const isChecked = e.target.checked;
  const currentDate = format(new Date(), 'dd.MM.yyyy');

  const obj = examItem;

  if (
    obj.chapter &&
    obj.chapter[purentIndex] &&
    obj.chapter[purentIndex].points &&
    obj.chapter[purentIndex].points[pointIndex]
  ) {
    const updatedObj = { ...obj };
    const targetPoint = updatedObj.chapter[purentIndex].points[pointIndex];

    targetPoint.itemVerification = isChecked;
    targetPoint.dataItemVerification = isChecked ? currentDate : null;

    try {
      const chapter = updatedObj.chapter;
      update(ref(database, `Students/${mail}/materials/${obj.id}`), {
        chapter,
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    console.error('One of the properties does not exist!');
    const error = new Error('One of the properties does not exist!');
    throwErrorRollbar(error);
  }
};

export const setStudentDateCompleted = (e, mail, examItem) => {
  const isChecked = e.target.checked;
  const currentDate = format(new Date(), 'dd.MM.yyyy');

  const obj = examItem;

  if (obj.courseCompletion) {
    const updatedObj = { ...obj };
    const targetPoint = updatedObj.courseCompletion;

    targetPoint.studentCheked = isChecked;
    targetPoint.student = isChecked ? currentDate : null;

    try {
      const courseCompletion = updatedObj.courseCompletion;
      const now = new Date();
      const formattedTime = format(now, 'dd MM yyyy HH:mm');
      update(ref(database, `Students/${mail}/materials/${obj.id}`), {
        courseCompletion,
      });

      update(ref(database, `Teachers/${examItem.mail}/notification`), {
        [examItem.id + 'studentDateCompleted']: {
          id: examItem.id,
          idItem: examItem.id,
          mail: mail,
          name: examItem.name,
          time: formattedTime,
          subject: subjectsObj[examItem.subject],
          type: 'materials interative-exum studentDateCompleted',
          viewed: true,
        },
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    console.error('One of the properties does not exist!');
    const error = new Error('One of the properties does not exist!');
    throwErrorRollbar(error);
  }
};

export const setExamenerDateCompleted = (e, mail = location.state.studentId, examItem) => {
  const isChecked = e.target.checked;
  const currentDate = format(new Date(), 'dd.MM.yyyy');

  const obj = examItem;

  if (obj.courseCompletion) {
    const updatedObj = { ...obj };
    const targetPoint = updatedObj.courseCompletion;

    targetPoint.examenerCheked = isChecked;
    targetPoint.examener = isChecked ? currentDate : null;

    try {
      const courseCompletion = updatedObj.courseCompletion;
      update(ref(database, `Students/${mail}/materials/${obj.id}`), {
        courseCompletion,
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    console.error('One of the properties does not exist!');
    const error = new Error('One of the properties does not exist!');
    throwErrorRollbar(error);
  }
};

export const setSupervisorDateCompleted = (e, mail = location.state.studentId, examItem) => {
  const isChecked = e.target.checked;
  const currentDate = format(new Date(), 'dd.MM.yyyy');

  const obj = examItem;

  if (obj.courseCompletion) {
    const updatedObj = { ...obj };
    const targetPoint = updatedObj.courseCompletion;

    targetPoint.supervisorCheked = isChecked;
    targetPoint.supervisor = isChecked ? currentDate : null;

    try {
      const courseCompletion = updatedObj.courseCompletion;
      update(ref(database, `Students/${mail}/materials/${obj.id}`), {
        courseCompletion,
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    console.error('One of the properties does not exist!');
    const error = new Error('One of the properties does not exist!');
    throwErrorRollbar(error);
  }
};

// export const setStudentDateStartExam = (e, mail, examItem) => {
//   const isChecked = e.target.checked;
//   const currentDate = format(new Date(), 'dd.MM.yyyy');

//   const obj = examItem;

//   if (obj.courseCompletion) {
//     const updatedObj = { ...obj };
//     const targetPoint = updatedObj.courseCompletion;

//     targetPoint.studentStartExamCheked = isChecked;
//     targetPoint.studentStartExam = isChecked ? currentDate : null;

//     try {
//       const courseCompletion = updatedObj.courseCompletion;
//       const now = new Date();
//       const formattedTime = format(now, 'dd MM yyyy HH:mm');
//       update(ref(database, `Students/${mail}/materials/${obj.id}`), {
//         courseCompletion,
//       });
//       console.log(formattedTime);

//       update(ref(database, `Teachers/${examItem.mail}/notification`), {
//         [examItem.id + 'studentDateStart']: {
//           id: examItem.id,
//           idItem: examItem.id,
//           mail: mail,
//           name: examItem.name,
//           time: formattedTime,
//           subject: subjectsObj[examItem.subject],
//           type: 'materials interative-exum studentDateStart',
//           viewed: true,
//         },
//       });

//     } catch (error) {
//       console.log(error);
//       throwErrorRollbar(error);
//     }
//   } else {
//     console.error('error');
//     throwErrorRollbar(error);
//   }
// };

export const setStudentDateEndExam = (e, mail, examItem) => {
  const isChecked = e.target.checked;
  const currentDate = format(new Date(), 'dd.MM.yyyy');

  const obj = examItem;

  if (obj.courseCompletion) {
    const updatedObj = { ...obj };
    const targetPoint = updatedObj.courseCompletion;

    targetPoint.studentEndExamCheked = isChecked;
    targetPoint.studentEndExam = isChecked ? currentDate : null;

    try {
      const courseCompletion = updatedObj.courseCompletion;
      update(ref(database, `Students/${mail}/materials/${obj.id}`), {
        courseCompletion,
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    console.error('error');
    throwErrorRollbar(error);
  }
};

export const setCommentExem = (comment, mail, examItem, parentIndex, pointIndex, setExamItem) => {
  const updatedObj = { ...examItem };

  if (
    updatedObj.chapter &&
    updatedObj.chapter[parentIndex] &&
    updatedObj.chapter[parentIndex].points &&
    updatedObj.chapter[parentIndex].points[pointIndex]
  ) {
    const targetPoint = {
      ...updatedObj.chapter[parentIndex].points[pointIndex],
    };

    const date = format(new Date(), 'HH:mm');
    targetPoint.comments = targetPoint.comments || [];
    targetPoint.comments.push({
      pointComment: comment,
      commentTime: date,
    });

    try {
      updatedObj.chapter[parentIndex].points[pointIndex] = targetPoint;

      setExamItem(prevExamItem => {
        const updatedChapter = [...prevExamItem.chapter];
        updatedChapter[parentIndex] = {
          ...updatedChapter[parentIndex],
          points: [...updatedChapter[parentIndex].points],
        };
        updatedChapter[parentIndex].points[pointIndex] = targetPoint;

        return {
          ...prevExamItem,
          chapter: updatedChapter,
        };
      });

      const chapter = updatedObj.chapter;

      update(ref(database, `Students/${mail}/materials/${updatedObj.id}`), {
        chapter,
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  } else {
    console.error('One of the properties does not exist!');
    const error = new Error('One of the properties does not exist!');
    throwErrorRollbar(error);
  }
};

export const deleteCommentExem = async (mail, examItem, parentIndex, pointIndex, commentIndex, setExamItem) => {
  try {
    const updatedChapter = [...examItem.chapter];
    const targetPoint = updatedChapter[parentIndex].points[pointIndex];

    if (targetPoint.comments && targetPoint.comments.length > commentIndex) {
      targetPoint.comments.splice(commentIndex, 1);
    } else {
      console.error('Comment not found at specified index');
      const error = new Error('One of the properties does not exist!');
      throwErrorRollbar(error);
      return;
    }

    try {
      await update(ref(database, `Students/${mail}/materials/${examItem.id}`), {
        chapter: updatedChapter,
      });
    } catch (updateError) {
      console.log(updateError);
      throwErrorRollbar(updateError);
    }

    setExamItem(prevExamItem => ({
      ...prevExamItem,
      chapter: updatedChapter,
    }));
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const setStudentPhotoExum = (file, fileName, mail, examItem, purentIndex, pointIndex) => {
  const id = uuidv4();

  const storageRef = MyRef(myStorage, 'Ответы-студентов-контрольный-лист/' + mail + '/' + id + '/' + fileName);
  const uploadTask = uploadBytesResumable(storageRef, file);

  uploadTask.on(
    'state_changed',
    snapshot => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

      store.dispatch(setProgressFile(String(progress).split('.')[0]));

      switch (snapshot.state) {
        case 'paused':
          console.log('Upload is paused');
          break;
        case 'running':
          console.log('Upload is running');
          break;
      }
    },
    error => {
      switch (error.code) {
        case 'storage/unauthorized':
          break;
        case 'storage/canceled':
          break;
        case 'storage/unknown':
          break;
      }
    },
    () => {
      getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
        store.dispatch(setProgressFile(0));
        const obj = examItem;
        // Проверяем существование необходимых свойств
        if (
          obj.chapter &&
          obj.chapter[purentIndex] &&
          obj.chapter[purentIndex].points &&
          obj.chapter[purentIndex].points[pointIndex]
        ) {
          // Создаем копию объекта
          const updatedObj = { ...obj };
          const targetPoint = updatedObj.chapter[purentIndex].points[pointIndex];

          // Обновляем свойство fileItems
          if ('fileItems' in targetPoint) {
            targetPoint.fileItems.push(downloadURL);
          } else {
            targetPoint.fileItems = [downloadURL];
          }

          console.log(updatedObj);
        } else {
          console.error('One of the properties does not exist!');
          const error = new Error('One of the properties does not exist!');
          throwErrorRollbar(error);
        }
        try {
          const now = new Date();
          const formattedTime = format(now, 'dd MM yyyy HH:mm');
          const chapter = obj.chapter;
          update(ref(database, `Students/${mail}/materials/${obj.id}`), {
            chapter,
          });

          update(ref(database, `Teachers/${examItem.mail}/notification`), {
            [examItem.id]: {
              id: examItem.id,
              idItem: examItem.id,
              mail: mail,
              name: examItem.name,
              time: formattedTime,
              point: pointIndex + 1,
              subject: subjectsObj[examItem.subject],
              type: 'materials interative-exum setStudentPhoto',
              viewed: true,
            },
          });
        } catch (error) {
          console.log(error);
          throwErrorRollbar(error);
        }
      });
    },
  );
};

export const deletStudentPhotoExum = async (item, mail, examItem, purentIndex, pointIndex) => {
  const stringDelete = item; // Непосредственно используем item как URL

  const desertRef = MyRef(myStorage, stringDelete);

  try {
    await deleteObject(desertRef)
      .then(() => {
        console.log('File deleted from storage');
      })
      .catch(error => {
        console.log(error);
        throwErrorRollbar(error);
      });

    // Логика обновления объекта
    const obj = examItem;
    if (
      obj.chapter &&
      obj.chapter[purentIndex] &&
      obj.chapter[purentIndex].points &&
      obj.chapter[purentIndex].points[pointIndex]
    ) {
      const updatedObj = { ...obj };
      const targetPoint = updatedObj.chapter[purentIndex].points[pointIndex];

      const urlToRemove = item;
      const indexToRemove = targetPoint.fileItems.indexOf(urlToRemove);

      if (indexToRemove !== -1) {
        targetPoint.fileItems.splice(indexToRemove, 1);
        console.log('Updated Object:', updatedObj);

        try {
          const chapter = obj.chapter;
          await update(ref(database, `Students/${mail}/materials/${obj.id}`), {
            chapter,
          });
          console.log('Database updated successfully');
        } catch (updateError) {
          console.log(updateError);
          throwErrorRollbar(updateError);
        }
      } else {
        console.log('URL to remove not found in object:', urlToRemove);
      }
    } else {
      console.error('One of the properties does not exist!');
      const error = new Error('One of the properties does not exist!');
      throwErrorRollbar(error);
    }
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const setStudentPhoto = (
  file,
  fileName,
  mail,
  subItems,
  itemId,
  parentItemIndex,
  subItemIndex,
  elementHomework,
) => {
  const id = uuidv4();

  const now = new Date();
  const formattedTime = format(now, 'dd MM yyyy HH:mm');

  const storageRef = MyRef(myStorage, 'Ответы-студентов/' + mail + '/' + itemId + '/' + fileName + id);
  const uploadTask = uploadBytesResumable(storageRef, file);

  uploadTask.on(
    'state_changed',
    snapshot => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

      store.dispatch(setProgressFile(String(progress).split('.')[0]));

      switch (snapshot.state) {
        case 'paused':
          console.log('Upload is paused');
          break;
        case 'running':
          console.log('Upload is running');
          break;
      }
    },
    error => {
      switch (error.code) {
        case 'storage/unauthorized':
          break;
        case 'storage/canceled':
          break;
        case 'storage/unknown':
          break;
      }
    },
    () => {
      getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
        store.dispatch(setProgressFile(0));

        if (subItems) {
          const stateFirst = store.getState();
          const selectorFirst = stateFirst.uploadFile.filesObject;

          //Если по радительскому индексу(ключу) в объекте нечего нет то записываю сюда объект с массивом
          if (typeof selectorFirst[parentItemIndex] === 'undefined') {
            const copiedObject = JSON.parse(JSON.stringify(selectorFirst));
            copiedObject[parentItemIndex] = { [subItemIndex]: [downloadURL] };
            store.dispatch(setObjectFiles(copiedObject));
          }

          //Если по радительскому индексу(ключу) в объекте есть елемент то и он не является массивом и по индексу есть елемент записываю в массив елемент
          if (
            typeof selectorFirst[parentItemIndex] !== 'undefined' &&
            !Array.isArray(selectorFirst[parentItemIndex]) &&
            typeof selectorFirst[parentItemIndex][subItemIndex] !== 'undefined'
          ) {
            const lengthItem = selectorFirst[parentItemIndex][subItemIndex].length;
            const copiedObject = JSON.parse(JSON.stringify(selectorFirst));
            copiedObject[parentItemIndex][subItemIndex][lengthItem] = downloadURL;
            store.dispatch(setObjectFiles(copiedObject));
          }

          //Если по радительскому индексу(ключу) в объекте есть елемент то и он не является массивом записываю сюда по сабИндексу(ключу) массив с елементом
          if (
            typeof selectorFirst[parentItemIndex] !== 'undefined' &&
            !Array.isArray(selectorFirst[parentItemIndex]) &&
            typeof selectorFirst[parentItemIndex][subItemIndex] === 'undefined'
          ) {
            const copiedObject = JSON.parse(JSON.stringify(selectorFirst));
            copiedObject[parentItemIndex][subItemIndex] = [downloadURL];
            store.dispatch(setObjectFiles(copiedObject));
          }
        } else {
          const stateFirst = store.getState();
          const selectorFirst = stateFirst.uploadFile.filesObject;

          //Если по радительскому индексу(ключу) в объекте нечего нет то записываю сюда  массив
          if (typeof selectorFirst[parentItemIndex] === 'undefined') {
            const copiedObject = JSON.parse(JSON.stringify(selectorFirst));
            copiedObject[parentItemIndex] = [downloadURL];
            store.dispatch(setObjectFiles(copiedObject));
          }

          //Если по радительскому индексу(ключу) в объекте есть элемент и он массив то записываю элемент
          if (typeof selectorFirst[parentItemIndex] !== 'undefined' && Array.isArray(selectorFirst[parentItemIndex])) {
            const lengthItem = selectorFirst[parentItemIndex].length;
            const copiedObject = JSON.parse(JSON.stringify(selectorFirst));
            copiedObject[parentItemIndex][lengthItem] = downloadURL;
            store.dispatch(setObjectFiles(copiedObject));
          }
        }

        const stateSecond = store.getState();
        const selectorSecond = stateSecond.uploadFile.filesObject;
        const copiedObjectSubmit = JSON.parse(JSON.stringify(selectorSecond));
        console.log('selectorSecond', copiedObjectSubmit);

        try {
          update(ref(database, `Students/${mail}/homeworks/${itemId}/studentPhotos`), {
            [parentItemIndex]: copiedObjectSubmit[parentItemIndex],
          });

          if (subItems) {
            console.log(elementHomework);

            update(ref(database, `Teachers/${elementHomework[subItemIndex].mail}/notification`), {
              [itemId + subItemIndex + 'File']: {
                id: itemId + subItemIndex + 'File',
                idItem: examItem.id,
                mail: mail,
                name: elementHomework[subItemIndex].name,
                titles: elementHomework[subItemIndex].titles,
                time: formattedTime,
                point: subItemIndex + 1,
                subject: elementHomework[subItemIndex].subject,
                type: 'homeworks-student homeworks-points setItemPointHomeworkFile',
                viewed: true,
              },
            });
          } else {
            update(ref(database, `Teachers/${elementHomework.mail}/notification`), {
              [itemId + 'File']: {
                id: itemId + 'File',
                idItem: examItem.id,
                mail: mail,
                name: elementHomework.name,
                time: formattedTime,
                subject: elementHomework.subject,
                type: 'homeworks-student homework setItemPointHomeworkFile',
                viewed: true,
              },
            });
          }
        } catch (error) {
          console.log(error);
          throwErrorRollbar(error);
        }
      });
    },
  );
};

export const deletObj = async item => {
  const stringDelete = item._location.path_;

  const desertRef = MyRef(myStorage, stringDelete);

  try {
    await deleteObject(desertRef)
      .then(() => {
        console.log('deleted');
      })
      .catch(error => {
        console.log(error);
        throwErrorRollbar(error);
      });
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const deleteFilesStudentHomeworks = async (email, id) => {
  const storageRef = MyRef(myStorage, `Ответы-студентов/${email}/${id}`);

  listAll(storageRef)
    .then(res => {
      res.items.forEach(itemRef => {
        deletObj(itemRef);
      });
    })
    .catch(error => {
      console.log(error);
      throwErrorRollbar(error);
    });
};

export const deletObjFile = async (item, itemParent, mail, copiedObject, itemKey, fileListSubIndex, actionFullHM) => {
  const storageRef = MyRef(myStorage, item);
  const stringDelete = storageRef._location.path_;

  const desertRef = MyRef(myStorage, stringDelete);

  try {
    await deleteObject(desertRef)
      .then(() => {
        console.log('deleted');
        try {
          if (actionFullHM !== 'fullHM') {
            if (copiedObject[itemKey][fileListSubIndex].length > 0) {
              update(ref(database, `Students/${mail}/homeworks/${itemParent.id}/studentPhotos`), {
                [itemKey]: copiedObject[itemKey],
              });
            } else {
              update(ref(database, `Students/${mail}/homeworks/${itemParent.id}`), {
                studentPhotos: false,
              });
            }
          } else {
            if (copiedObject[itemKey].length > 0) {
              update(ref(database, `Students/${mail}/homeworks/${itemParent.id}/studentPhotos`), {
                [itemKey]: copiedObject[itemKey],
              });
            } else {
              update(ref(database, `Students/${mail}/homeworks/${itemParent.id}`), {
                studentPhotos: false,
              });
            }
          }
        } catch (error) {
          console.log(error);
        }
      })
      .catch(error => {
        console.log(error);
        throwErrorRollbar(error);
      });
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

export const getHomeworkContent = async subject => {
  const q = query(collection(firestore, 'homeworks-content'), where('subject', '==', subject));

  const querySnapshot = await getDocs(q);

  let objContent = {};

  const content = querySnapshot.docs.map(doc => {
    if (doc.exists()) {
      const ops = JSON.parse(doc.data().content);
      const id = doc.data().id;
      objContent[doc.data().id] = doc.data();
      return { ops: ops, id: id };
    } else {
      console.log('No such document!');
      return null;
    }
  });

  // console.log('===================');
  // console.log(content);
  // console.log(objContent);
  return objContent;
};

export const getHomeworkContentStudent = async subject => {
  const q = query(collection(firestore, 'homeworks-content-student'), where('subject', '==', subject));

  const querySnapshot = await getDocs(q);

  let objContent = {};

  const content = querySnapshot.docs.map(doc => {
    if (doc.exists()) {
      const ops = JSON.parse(doc.data().content);
      const id = doc.data().id;
      objContent[doc.data().id] = doc.data();
      return { ops: ops, id: id };
    } else {
      console.log('No such document!');
      return null;
    }
  });
  return objContent;
};

export const resetCaches = async () => {
  try {
    goOffline(database);
    goOnline(database);
    console.log(database);
  } catch (error) {
    console.error('Error resetting caches:', error);
  }

  const connectedRef = ref(database, '.info/connected');
  onValue(connectedRef, snap => {
    if (snap.val() === true) {
      console.log('Connected to Firebase Database');
    } else {
      console.log('Disconnected from Firebase Database');
    }
  });
};

export const setStudentHomeworkToGroup = (
  currentHomework,
  groupOfStudents,
  numbHomework,
  deadline,
  selectedStudentsGroup,
) => {
  try {
    const groupId = selectedStudentsGroup.id;

    update(ref(database, `StudentsGroup/${groupId}/homeworks`), {
      [currentHomework.id]: {
        content: `homeworks-content-student/${currentHomework.id}`,
        id: currentHomework.id,
        name: currentHomework.name,
        mail: currentHomework.mail,
        titles: currentHomework.titles,
        notebook: currentHomework.notebook,
        subject: subjectsObj[currentHomework.subject],
        completed: currentHomework.completed,
        deadline: deadline,
        subItems: currentHomework.subItems,
        subArrayCheckeds: currentHomework.subArrayCheckeds,
        studentPhotos: false,
        countNumbHomework: numbHomework,
        teacherCheckeds: currentHomework.teacherCheckeds,
      },
    });
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }

  groupOfStudents.forEach(async student => {
    const idMail = currentHomework.id + student;
    (async () => {
      const docRef = doc(firestore, 'homeworks-content', currentHomework.id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        data.id = idMail;
        setDoc(doc(firestore, 'homeworks-content-student/', idMail), data);
      } else {
        console.log('No such document!');
      }
    })();

    try {
      update(ref(database, `Students/${student}/homeworks`), {
        [currentHomework.id + student]: {
          content: `homeworks-content-student/${currentHomework.id + student}`,
          id: currentHomework.id + student,
          name: currentHomework.name,
          mail: currentHomework.mail,
          titles: currentHomework.titles,
          notebook: currentHomework.notebook,
          subject: subjectsObj[currentHomework.subject],
          completed: currentHomework.completed,
          deadline: deadline,
          subItems: currentHomework.subItems,
          subArrayCheckeds: currentHomework.subArrayCheckeds,
          studentPhotos: false,
          countNumbHomework: numbHomework,
          teacherCheckeds: currentHomework.teacherCheckeds,
        },
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }

    try {
      const now = new Date();
      const formattedTime = format(now, 'dd MM yyyy HH:mm');

      update(ref(database, `Students/${student}/notification`), {
        [currentHomework.id + student]: {
          id: currentHomework.id + student,
          idItem: currentHomework.id + student,
          name: currentHomework.name,
          subject: subjectsObj[currentHomework.subject],
          time: formattedTime,
          type: 'homeworks',
          viewed: true,
        },
      });
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  });
};

export const getAllNotebooks = async () => {
  try {
    const res = await get(ref(database, 'Notebooks'));
    return res.val();
  } catch (error) {
    console.log(error);
    throwErrorRollbar(error);
  }
};

const setUsersStore = () => {
  const studentsPromise = getStudents();
  const teachersPromise = getTeachers();
  const notebooksPromise = getAllNotebooks();
  const materialsPromise = getMaterials();
  const allStatistics = getStatistics();

  Promise.all([studentsPromise, teachersPromise, notebooksPromise, materialsPromise, allStatistics])
    .then(results => {
      store.dispatch(setStudentsState(results[0]));
      store.dispatch(setTeachersState(results[1]));
      store.dispatch(setNotebooksState(results[2]));
      store.dispatch(setMaterialsState(JSON.stringify(results[3])));
      store.dispatch(setStatisticsAllState(results[4]));
    })
    .catch(error => {
      console.error('An error occurred: ', error);
    });
};

try {
  setTimeout(() => {
    setUsersStore();
  }, 5000);
} catch (error) {
  console.error('Error updating session times', error);
}

//========================================================================================

export const startNewSession = async user => {
  try {
    return await recordNewSession(
      user,
      path => get(ref(database, path)),
      (path, updater) => runTransaction(ref(database, path), updater, { applyLocally: false }),
    );
  } catch (error) {
    console.error('Error updating session times', error);
    return false;
  }
};

//========================================================================================

export const setOpenMaterialFlag = async (mail, studentItem, id) => {
  try {
    await update(ref(database, `Students/${mail}/${studentItem}/${id}`), {
      materialOpen: true,
    });
  } catch (error) {
    console.error('Error updating link generation count', error);
  }
};

export const updateStatisticsFunc = async (urlItemKey, action) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const currentMonth = months[currentDate.getMonth()];

  const res = await get(ref(database, `Statistics/${urlItemKey}/${currentYear}/${currentMonth}`));

  const currentValue = res.val();

  const updatedValue = action === 'increment' ? currentValue + 1 : currentValue - 1;

  await update(ref(database, `Statistics/${urlItemKey}/${currentYear}`), {
    [currentMonth]: updatedValue,
  });
};
